import { ApiClient } from "@hakili/api-client";
import {
  type Identity,
  identityFrom,
  messageOf,
  refused,
  type Session,
  signedIn,
  signedOut,
  START,
} from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * `round C5-2`, `M1`: **the cabinet's session, on its own surface.**
 *
 * The expert app carried the no-login tour link alone (`C5.0`); stage 2 gives
 * it a login, a session and « Se déconnecter » (6.2, on every screen). The
 * shape is the bank's (`bank-web/session/useSession.ts`): `ApiClient` with the
 * surface declared , `expert`, the fifth , the refresh cookie the api names
 * for it, `/me` for the identity, the transitions from `@hakili/app-shell`.
 *
 * **This surface admits a cabinet's people and nobody else** (6.6's second
 * layer, `P0-DLW-004`'s pattern): `/me` answers `organisation.type`, and an
 * authenticated principal of another kind is anonymous TO THIS SURFACE, told
 * why in one sentence. The role decides the SHELL , the field expert's phone,
 * the responsable's desk , and is read from the same answer.
 */

export interface ExpertSession {
  readonly session: Session;
  readonly connecter: (credentials: { email: string; password: string }) => Promise<void>;
  readonly deconnecter: () => Promise<void>;
  /** After an invitation was accepted: the access token the accept door minted. */
  readonly adopter: (accessToken: string) => Promise<void>;
  readonly api: ApiClient;
}

/** The two chairs of a cabinet, by the role `/me` answers; `null` for any other principal. */
export const chairOf = (role: string): "responsable" | "expert" | null => {
  if (role === "responsableCabinet") return "responsable";
  if (role === "expertTerrain") return "expert";
  return null;
};

export function useSession(origin: string, fetchImpl?: typeof globalThis.fetch): ExpertSession {
  const api = useMemo(
    () =>
      new ApiClient({
        origin,
        surface: "expert",
        ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
      }),
    [origin, fetchImpl],
  );
  const [session, setSession] = useState<Session>(START);
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const identify = useCallback(async (): Promise<void> => {
    try {
      const me = await api.request<unknown>("/api/v1/me", "get");
      const identity: Identity | null = identityFrom(me);
      if (identity === null) {
        setSession(signedOut(t("expert.session.inattendue")));
        return;
      }
      if (identity.organisationType !== "cabinet" || chairOf(identity.role) === null) {
        // Somebody the api knows and this surface will not seat: said, not redirected.
        api.setAccessToken(null);
        setSession(refused(t("common.surface.refusCabinet")));
        return;
      }
      setSession(signedIn(identity));
    } catch (failure) {
      setSession(signedOut(messageOf(failure, t("common.error.reseau"))));
    }
  }, [api]);

  useEffect(() => {
    void (async () => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/refresh", "post");
        if (!live.current) return;
        if (typeof tokens.accessToken === "string") {
          api.setAccessToken(tokens.accessToken);
          await identify();
          return;
        }
        setSession(signedOut());
      } catch {
        if (live.current) setSession(signedOut());
      }
    })();
  }, [api, identify]);

  const connecter = useCallback(
    async (credentials: { email: string; password: string }): Promise<void> => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/staff/login", "post", {
          body: credentials,
        });
        if (typeof tokens.accessToken !== "string") {
          setSession(refused(t("expert.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        await identify();
      } catch (failure) {
        setSession(refused(messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api, identify],
  );

  const adopter = useCallback(
    async (accessToken: string): Promise<void> => {
      api.setAccessToken(accessToken);
      await identify();
    },
    [api, identify],
  );

  const deconnecter = useCallback(async (): Promise<void> => {
    try {
      await api.request<unknown>("/api/v1/auth/logout", "post");
    } catch {
      // The server may already have forgotten this session; the client
      // forgets its own token either way, which is the half that matters.
    }
    api.setAccessToken(null);
    setSession(signedOut());
  }, [api]);

  return useMemo(
    () => ({ session, connecter, deconnecter, adopter, api }),
    [session, connecter, deconnecter, adopter, api],
  );
}
